import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Alert, Box, Button, CircularProgress } from "@mui/material";

import TopToolbar from "../../components/TopToolbar";
import { getSharedConversationById } from "../../components/ShareConversation/requests";
import { useLanguage } from "../../contexts/LanguageContext";
import { ROUTES } from "../../common/constant";

/**
 * Entry point for a shared conversation link (`/shared-with/:conversationId`).
 * Shared conversations are viewed in the full patient page, so this resolves the
 * conversation's short code and redirects to `/c/:shortCode`.
 */
const SharedConversation: React.FC = () => {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    if (!conversationId) return;
    let active = true;
    getSharedConversationById(conversationId)
      .then((res) => {
        if (!active) return;
        const shortCode = res.data?.short_code;
        if (shortCode) {
          navigate(`${ROUTES.CONVERSATION}/${shortCode}`, { replace: true });
        } else {
          setError(true);
        }
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [conversationId, navigate]);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <TopToolbar />
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {error ? (
          <Box sx={{ textAlign: "center" }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t("share.unavailable")}
            </Alert>
            <Button onClick={() => navigate(ROUTES.DASHBOARD)} sx={{ textTransform: "none" }}>
              {t("share.backToDashboard")}
            </Button>
          </Box>
        ) : (
          <CircularProgress />
        )}
      </Box>
    </Box>
  );
};

export default SharedConversation;
